// Static definition of the AnyTech365 Sales Call Scorecard (mirrors the
// reference spreadsheet: dimensions + weights, behavioral flags, dropdown
// lists, rating bands and the manager guide).

export const SCORE_LEVELS = [
    { value: 0, label: "Not demonstrated", short: "None" },
    { value: 1, label: "Weak", short: "Weak" },
    { value: 2, label: "Good", short: "Good" },
    { value: 3, label: "Excellent", short: "Excellent" },
];

// Weights add up to 1 (100%).
export const DIMENSIONS = [
    { id: 1, name: "Opening, Confidence & First Impression", weight: 0.05, leakage: "Opening / Trust", description: "Professional/natural opening; ownership; clear direction; trust; confident and not robotic." },
    { id: 2, name: "Voice, Tone & Verbal Presence", weight: 0.07, leakage: "Voice / Verbal Presence", description: "Tone, pace, volume, articulation, energy, authority, pauses/silence; avoids weak filler language and nervous delivery." },
    { id: 3, name: "Rapport, Active Listening & Adaptation", weight: 0.06, leakage: "Rapport / Active Listening", description: "Adapts pace/language; acknowledges concerns; does not interrupt; remembers and reuses customer information naturally." },
    { id: 4, name: "Discovery & Qualification", weight: 0.10, leakage: "Discovery", description: "Understands reason, situation, history, impact, wider needs, priorities and decision factors. Opens with open questions, then probes, clarifies and confirms." },
    { id: 5, name: "Questioning & Call Control", weight: 0.06, leakage: "Call Control", description: "Questions have purpose; agent guides without dominating; summarizes; redirects professionally; controls transitions." },
    { id: 6, name: "Remote Connection & Scan Positioning", weight: 0.06, leakage: "Scan Positioning", description: "Explains why remote diagnosis/scan is useful; customer understands purpose; scan is not presented as predetermined proof of a sale." },
    { id: 7, name: "Scan Execution & Interpretation", weight: 0.10, leakage: "Scan Interpretation", description: "Correctly reads results; separates relevant findings from noise; understands severity/relevance; does not inflate counts or findings." },
    { id: 8, name: "Problem Awareness & Priority Shift", weight: 0.10, leakage: "Problem Awareness", description: "Connects findings to customer situation; explains impact; confirms understanding; legitimately helps customer reassess priorities." },
    { id: 9, name: "Urgency Creation & Integrity", weight: 0.05, leakage: "Urgency", description: "Urgency is proportional to verified findings; realistic consequences; no unsupported predictions or fear tactics." },
    { id: 10, name: "Diagnosis to Commercial Transition", weight: 0.06, leakage: "Commercial Transition", description: "Natural bridge from the issue to the finding, the impact, the need and then the solution. No abrupt switch from technical support to pitch." },
    { id: 11, name: "Value Creation & Personalization", weight: 0.10, leakage: "Value Creation", description: "Sells outcomes; connects benefits to discovery/scan; builds value before price; coherent ATC365 relevant solution." },
    { id: 12, name: "Solution & Price Presentation", weight: 0.06, leakage: "Solution Presentation", description: "Right solution/duration; clear offer; confident price delivery; no apologetic pricing; asks and uses closing silence." },
    { id: 13, name: "Objection Handling & Reframing", weight: 0.06, leakage: "Objection Handling", description: "Acknowledges, clarifies, isolates, responds, confirms and re-closes. Diagnoses the real objection before answering/discounting." },
    { id: 14, name: "Buying Signals & Closing", weight: 0.05, leakage: "Closing", description: "Recognizes buying signals; trial/direct closes; appropriate persistence; re-closes; knows when to stop talking." },
    { id: 15, name: "Commercial Outcome & Next Step", weight: 0.02, leakage: "Genuine Customer / Lead Limitation", description: "Clear outcome and next action; sale/follow-up/refusal/eligibility properly established." },
];

export const BEHAVIORAL_FLAGS = [
    "Talks excessively",
    "Interrupts customer",
    "Doesn't listen to answers",
    "Sounds scripted / robotic",
    "Low confidence / authority",
    "Excessive technical language",
    "Reads scan results without explaining",
    "Exaggerates scan findings",
    "Creates unjustified urgency",
    "Price before sufficient value",
    "Discounts too quickly",
    "Misses buying signal",
    "Accepts first objection",
    "Fails to isolate objection",
    "Doesn't re-close",
    "Negotiates against themselves",
    "Breaks closing silence",
    "No clear close attempted",
].map((label, index) => ({ id: index + 1, label }));

export const OUTCOMES = [
    "Sale",
    "Qualified follow-up",
    "Genuine customer refusal",
    "Not commercially eligible",
    "Agent abandoned opportunity",
];

export const LEAKAGES = [
    "Opening / Trust",
    "Voice / Verbal Presence",
    "Rapport / Active Listening",
    "Discovery",
    "Call Control",
    "Scan Positioning",
    "Scan Interpretation",
    "Problem Awareness",
    "Priority Shift",
    "Urgency",
    "Commercial Transition",
    "Value Creation",
    "Solution Presentation",
    "Price Presentation",
    "Objection Identification",
    "Objection Handling",
    "Buying Signal Missed",
    "Closing",
    "Genuine Customer / Lead Limitation",
];

export const OBJECTIONS = [
    "None", "Price", "Need", "Trust", "Timing", "Think about it",
    "Partner / spouse", "Competitor", "Previous experience", "Technical skepticism",
];

export const URGENCY_LEVELS = ["Appropriate", "Too weak", "Excessive"];
export const YES_NO_PARTIAL = ["Yes", "Partially", "No"];

// Total sales score (0-1) -> rating band. Checked from the highest threshold down.
export const RATING_BANDS = [
    { key: "excellent", label: "Excellent", min: 0.85, color: "var(--green)" },
    { key: "strong", label: "Strong", min: 0.7, color: "var(--main)" },
    { key: "developing", label: "Developing", min: 0.55, color: "var(--orange)" },
    { key: "critical", label: "Critical coaching", min: 0, color: "var(--pink)" },
];

export const STRONG_CALL_THRESHOLD = 0.7;
export const CRITICAL_CALL_THRESHOLD = 0.55;

export const MANAGER_GUIDE = [
    {
        title: "Scoring",
        levels: [
            { value: 0, text: "Not done, or damaging to the call" },
            { value: 1, text: "Weak" },
            { value: 2, text: "Acceptable" },
            { value: 3, text: "Strong" },
        ],
        text: "Score observable sales behavior, not personality.",
    },
    { title: "Core sales chain", steps: ["Trust", "Discovery", "Diagnosis", "Scan", "Interpretation", "Problem Awareness", "Priority Shift", "Appropriate Urgency", "Need", "Value", "Recommendation", "Price", "Objection", "Reframe", "Close"] },
    { title: "Scan principle", text: "The scan provides evidence. The agent interprets it. The customer understands the impact. Only then should the recommendation follow." },
    { title: "Urgency integrity", text: "Urgency must be proportional to verified findings. Do not reward exaggeration, unsupported predictions, fear tactics or misleading interpretations." },
    { title: "Priority shift", steps: ["Acknowledge the customer's original reason", "Introduce the verified finding", "Explain its significance", "Connect it to their situation", "Confirm understanding", "Establish the appropriate priority"] },
    { title: "Value test", text: "Ask: If the price disappeared from this call, did the agent make the customer want the solution?" },
    { title: "Objection framework", steps: ["Acknowledge", "Clarify", "Isolate", "Respond", "Confirm", "Re-close"] },
    { title: "Closing silence", text: "After presenting price / asking for commitment, allow the customer to respond. Do not negotiate against yourself." },
    { title: "Coaching rule", text: "Identify the exact pivotal timestamp and ONE priority behavior for the agent's next calls." },
    { title: "Calibration", text: "Before rollout, all managers should score the same 5–10 calls, compare differences and align on what 0/1/2/3 means." },
];
